import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ForgotPasswordFormData, forgotPasswordSchema } from '@/utils/validations';
import { useForgotPasswordMutation, useResetPasswordMutation } from '@/store/api/authApi';
import { useOtpTimer } from '@/hooks/useOtpTimer';

const getErrorMessage = (err: any, fallback: string) => {
  const message = err?.data?.message;
  if (Array.isArray(message)) return message[0] || fallback;
  return message || fallback;
};

export const useForgotPassword = (options?: { onSuccess?: () => void }) => {
  const { t } = useTranslation();
  const [showOtpField, setShowOtpField] = useState(false);
  const [phone, setPhone] = useState<{
    country?: string;
    countryCode: string;
    fullNumber: string;
    phoneNumber: string;
  }>({
    country: '',
    countryCode: '',
    fullNumber: '',
    phoneNumber: '',
  });

  const { timeLeft, active, start, reset: resetTimer } = useOtpTimer(600);

  const [forgotPassword, { isLoading: otpLoading }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: resetLoading }] = useResetPasswordMutation();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange',
    defaultValues: {
      mobile: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const { trigger, reset, resetField } = form;

  const handleResetOtpField = () => {
    setShowOtpField(false);
    resetTimer();
    resetField('otp');
  };

  const handleSendOtp = useCallback(async () => {
    const isValid = await trigger('mobile');
    if (!isValid) {
      toast.error(t('auth.forgotPassword.invalidPhoneToast'));
      return;
    }

    try {
      await forgotPassword({ mobile: phone.fullNumber }).unwrap();

      toast.success(t('auth.forgotPassword.otpSentToast', { phone: phone.fullNumber }));
      setShowOtpField(true);
      start();
    } catch (err: any) {
      toast.error(getErrorMessage(err, t('auth.forgotPassword.otpFailedToast')));
    }
  }, [trigger, phone, forgotPassword, start, t]);

  const handleSubmitForm = async (data: ForgotPasswordFormData) => {
    try {
      await resetPassword({
        mobile: data.mobile,
        code: data.otp,
        newPassword: data.newPassword,
      }).unwrap();

      toast.success(t('auth.forgotPassword.successToast'));
      reset();
      handleResetOtpField();
      options?.onSuccess?.();
    } catch (err: any) {
      const message = getErrorMessage(err, t('auth.forgotPassword.failedToast'));
      if (message.toLowerCase().includes('invalid')) {
        resetField('otp');
      }
      toast.error(message);
    }
  };

  return {
    form,

    // phone
    phone,
    setPhone,

    // otp
    showOtpField,
    timeLeft,
    active,

    // loaders
    otpLoading,
    resetLoading,

    // handlers
    handleSendOtp,
    handleResetOtpField,
    handleSubmitForm,
  };
};
